/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect } from 'react'
import CardComponent from 'pages/Front/Booking/components/CardComponent'
import { PercentBadgeIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useAppDispatch } from 'helpers/store'
import { useTranslation } from 'react-i18next'
import { PackagesCart } from 'types/entities/PackagesCart'
import { PackagesCartItemVoucher } from 'types/entities/PackagesCartItemVoucher'
import { currencyNumberFormat } from 'utils/utility'
import { usePackagesCart } from 'utils/packageUtils'
import { PackageItemItemType } from 'types/enums/PackageItemItemType'
import { useForm } from 'react-hook-form'
import { toast } from 'utils/toastUtils'
import { closeModal } from 'helpers/actions'
import ModalScrollableClose from 'components/ModalScrollableClose'
import SellerAction from 'store/seller/SellerAction'

interface BookingSellerVoucherModalProps {
    packagesCartItemVoucher: PackagesCartItemVoucher
    packagesCart: PackagesCart
}

const BookingSellerVoucherModal = (props: BookingSellerVoucherModalProps) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { priceByType } = usePackagesCart(props.packagesCart)
    const [isLoading, setIsLoading] = React.useState<boolean>(false)

    // const [upsellPriceDiscount, setUpsellPriceDiscount] = React.useState<number>(priceDiscount / 100 || 0);
    const [upsellPriceDiscount, setUpsellPriceDiscount] =
        React.useState<number>(props.packagesCartItemVoucher.price / 100 || 0)
    const inputClass =
        'flex rounded-md border leading-tight w-full py-1 px-2 h-[30px]'

    const { register, setValue, watch } = useForm({
        defaultValues: {
            priceDiscount: props.packagesCartItemVoucher.price / 100 || 0,
        },
    })
    const changeDiscountHandler = () => {
        if (isLoading) {
            return
        }

        setIsLoading(true)
        dispatch(
            SellerAction.fetchChangeVoucherPrice(
                props.packagesCart.id,
                props.packagesCartItemVoucher,
                'price',
                upsellPriceDiscount * 100
            )
        )
            .then((r) => {
                dispatch(closeModal())
                toast.success(t('Upsell changed'))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const priceDiscount = watch('priceDiscount')

    const onChangePriceDiscountHandler = (newValue: number) => {
        setUpsellPriceDiscount(+newValue)
    }

    useEffect(() => {
        onChangePriceDiscountHandler(+priceDiscount)
    }, [priceDiscount])

    return (
        <CardComponent
            className={clsx(
                'BookingSellerDiscountModal w-full md:min-w-[400px] max-w-[500px] !p-0 border border-gray-200 dark:border-gray-800 relative overflow-hidden h-auto max-h-full flex flex-col'
            )}
        >
            <div
                className={clsx(
                    'top-0 z-30 sticky flex flex-row justify-between rounded-tl-lg rounded-tr-lg w-full px-3 py-3 font-bold border-b-2 border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 drop-shadow-sm'
                )}
            >
                <div className={clsx('flex flex-row items-center')}>
                    <PercentBadgeIcon
                        className={clsx(
                            'w-5 h-5 mr-1 text-gray-600/70 dark:text-gray-400 drop-shadow-xl'
                        )}
                    />
                    <h4
                        className={clsx(
                            'font-medium text-gray-700 dark:text-gray-300'
                        )}
                    >
                        {t('Discount')}
                    </h4>
                </div>
                <ModalScrollableClose />
            </div>

            <div
                className={clsx(
                    'flex w-full flex-col px-3 pt-3 pb-4 bg-white dark:bg-gray-900 overflow-hidden h-auto rounded-bl-lg rounded-br-lg'
                )}
            >
                <div className={clsx('flex flex-col overflow-y-auto h-full')}>
                    <h4
                        className={clsx(
                            'font-bold text-lg text-gray-900 dark:text-gray-100'
                        )}
                    >
                        {t('Item information')}
                    </h4>
                    <div className={clsx('mb-6')}>
                        <table className={clsx('w-full')}>
                            <tbody>
                                <tr
                                    className={clsx(
                                        'border-b border-dashed border-gray-200 dark:border-gray-700'
                                    )}
                                >
                                    <td
                                        className={clsx(
                                            'font-medium text-gray-700 dark:text-gray-300'
                                        )}
                                    >
                                        {'Total price of items'}
                                    </td>
                                    <td
                                        className={clsx(
                                            'text-right text-gray-900 dark:text-gray-100'
                                        )}
                                    >
                                        {currencyNumberFormat(
                                            priceByType(
                                                PackageItemItemType.PackageItem
                                            )
                                        )}
                                    </td>
                                </tr>
                                <tr
                                    className={clsx(
                                        'border-b border-dashed border-gray-200 dark:border-gray-700'
                                    )}
                                >
                                    <td
                                        className={clsx(
                                            'font-medium text-gray-700 dark:text-gray-300'
                                        )}
                                    >
                                        {'Total price of upsells'}
                                    </td>
                                    <td
                                        className={clsx(
                                            'text-right text-gray-900 dark:text-gray-100'
                                        )}
                                    >
                                        {currencyNumberFormat(
                                            priceByType(
                                                PackageItemItemType.Upsell
                                            )
                                        )}
                                    </td>
                                </tr>
                                <tr
                                    className={clsx(
                                        'border-b border-dashed border-gray-200 dark:border-gray-700'
                                    )}
                                >
                                    <td
                                        className={clsx(
                                            'font-medium text-gray-700 dark:text-gray-300'
                                        )}
                                    >
                                        {'Current discount'}
                                    </td>
                                    <td
                                        className={clsx(
                                            'text-right text-gray-900 dark:text-gray-100'
                                        )}
                                    >
                                        {currencyNumberFormat(
                                            props.packagesCartItemVoucher.price
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        className={clsx(
                            'Actions grid grid-cols-3 gap-x-2 gap-y-3 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-3 shadow bg-gray-50 dark:bg-gray-800'
                        )}
                    >
                        <div className={clsx('cols-span-1')}>
                            <label
                                className={clsx(
                                    'inline-flex mb-1 font-bold text-gray-700 dark:text-gray-300'
                                )}
                                htmlFor={'priceDiscount'}
                            >
                                {t('CHF discount')}
                            </label>
                            <input
                                className={clsx(
                                    inputClass,
                                    'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
                                )}
                                {...register('priceDiscount')}
                                type="number"
                                min={0}
                                max={1000}
                                step={1}
                                placeholder={currencyNumberFormat(
                                    priceDiscount
                                )}
                                name="priceDiscount"
                                disabled={isLoading}
                            />
                        </div>

                        <div className={clsx('col-span-3 text-center')}>
                            <button
                                className={clsx(
                                    'border-green-400 bg-green-400 text-white mx-auto flex items-center justify-center font-bold leading-tight py-2 px-4 rounded-md',
                                    isLoading && 'opacity-50 cursor-not-allowed'
                                )}
                                onClick={changeDiscountHandler}
                                disabled={isLoading}
                            >
                                <CheckIcon
                                    className={clsx('w-5 h-5 flex-none mr-2')}
                                />
                                <span>
                                    {isLoading
                                        ? t('Processing...')
                                        : t('Apply discount')}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </CardComponent>
    )
}

export default BookingSellerVoucherModal
